import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";
import FloatingActions from "./components/FloatingActions";
// trackVisit is dynamically imported inside VisitTracker to keep the supabase
// client out of the critical entry chunk.

const NotFound = lazy(() => import("./pages/NotFound"));
const MentionsLegales = lazy(() => import("./pages/legal/MentionsLegales"));
const CGU = lazy(() => import("./pages/legal/CGU"));
const CGV = lazy(() => import("./pages/legal/CGV"));
const RGPD = lazy(() => import("./pages/legal/RGPD"));
const CookiesPage = lazy(() => import("./pages/legal/CookiesPage"));
const Lexique = lazy(() => import("./pages/Lexique"));
const ChargesPage = lazy(() => import("./pages/ChargesPage"));
const RdvServices = lazy(() => import("./pages/RdvServices"));
const CoursWOD = lazy(() => import("./pages/services/CoursWOD"));
const HybridTraining = lazy(() => import("./pages/services/HybridTraining"));
const CrossfitDebutant = lazy(() => import("./pages/services/CrossfitDebutant"));
const ReprendreLeSport = lazy(() => import("./pages/services/ReprendreLeSport"));
const LieuxHub = lazy(() => import("./pages/lieux/LieuxHub"));
const CommunePage = lazy(() => import("./pages/lieux/CommunePage"));
const QueRecherchezVousCrossfit = lazy(() => import("./pages/blog/QueRecherchezVousCrossfit"));
const QuestCeQueLeCrossfit = lazy(() => import("./pages/blog/QuestCeQueLeCrossfit"));
const CrossfitSenior = lazy(() => import("./pages/blog/CrossfitSenior"));
const CrossfitCoupleDebutant = lazy(() => import("./pages/blog/CrossfitCoupleDebutant"));
const AncienSportifCrossfit = lazy(() => import("./pages/blog/AncienSportifCrossfit"));
const CrossfitEnfants = lazy(() => import("./pages/blog/CrossfitEnfants"));
const CrossfitScienceSante = lazy(() => import("./pages/blog/CrossfitScienceSante"));
const PrixCrossfitBenchmark = lazy(() => import("./pages/blog/PrixCrossfitBenchmark"));
const GuideCompletCrossfit = lazy(() => import("./pages/blog/GuideCompletCrossfit"));
const SportSaintPierreChandieu = lazy(() => import("./pages/blog/SportSaintPierreChandieu"));
const CompetitionsCrossfitOpen = lazy(() => import("./pages/blog/CompetitionsCrossfitOpen"));
const AvisCrossfitChistera = lazy(() => import("./pages/blog/AvisCrossfitChistera"));
const CalculateurChargesCrossfit = lazy(() => import("./pages/blog/CalculateurChargesCrossfit"));
const SeanceCrossfit = lazy(() => import("./pages/blog/SeanceCrossfit"));

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function VisitTracker() {
  useEffect(() => {
    const run = () => {
      import("./lib/track-visit").then((m) => m.trackVisit()).catch(() => {});
    };
    const ric: (cb: () => void) => void =
      (window as any).requestIdleCallback?.bind(window) ??
      ((cb: () => void) => window.setTimeout(cb, 2000));
    ric(run);
  }, []);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <VisitTracker />
        <FloatingActions />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/legal/mentions" element={<MentionsLegales />} />
            <Route path="/legal/cgu" element={<CGU />} />
            <Route path="/legal/cgv" element={<CGV />} />
            <Route path="/legal/rgpd" element={<RGPD />} />
            <Route path="/legal/cookies" element={<CookiesPage />} />
            <Route path="/lexique" element={<Lexique />} />
            <Route path="/charges" element={<ChargesPage />} />
            <Route path="/rdv-services" element={<RdvServices />} />
            <Route path="/cours/wod" element={<CoursWOD />} />
            <Route path="/cours/hybrid-training" element={<HybridTraining />} />
            <Route path="/crossfit-debutant" element={<CrossfitDebutant />} />
            <Route path="/reprendre-le-sport" element={<ReprendreLeSport />} />
            <Route path="/lieux" element={<LieuxHub />} />
            <Route path="/lieux/:slug" element={<CommunePage />} />
            <Route path="/blog/que-recherchez-vous-dans-le-crossfit" element={<QueRecherchezVousCrossfit />} />
            <Route path="/blog/quest-ce-que-le-crossfit" element={<QuestCeQueLeCrossfit />} />
            <Route path="/blog/crossfit-senior" element={<CrossfitSenior />} />
            <Route path="/blog/crossfit-couple-debutant" element={<CrossfitCoupleDebutant />} />
            <Route path="/blog/ancien-sportif-crossfit" element={<AncienSportifCrossfit />} />
            <Route path="/blog/crossfit-enfants" element={<CrossfitEnfants />} />
            <Route path="/blog/crossfit-science-sante-benefices" element={<CrossfitScienceSante />} />
            <Route path="/blog/prix-crossfit-lyon-est-benchmark" element={<PrixCrossfitBenchmark />} />
            <Route path="/blog/guide-complet-crossfit" element={<GuideCompletCrossfit />} />
            <Route path="/blog/faire-du-sport-saint-pierre-de-chandieu" element={<SportSaintPierreChandieu />} />
            <Route path="/blog/competitions-crossfit-open-lyon" element={<CompetitionsCrossfitOpen />} />
            <Route path="/blog/avis-crossfit-chistera" element={<AvisCrossfitChistera />} />
            <Route path="/blog/calculateur-charges-crossfit-1rm" element={<CalculateurChargesCrossfit />} />
            <Route path="/blog/comment-se-passe-une-seance-crossfit" element={<SeanceCrossfit />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
